import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const [state, setState] = useState('working'); // working | ok | fail
  const [email, setEmail] = useState(''); const [resent, setResent] = useState(false); const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; ran.current = true;
    const token = params.get('token');
    if (!token) { setState('fail'); return; }
    axios.post(`${API}/auth/verify-email`, { token }, { withCredentials: true })
      .then(async () => { setState('ok'); try { await checkAuth(); } catch { /* not signed in — fine */ } })
      .catch(() => setState('fail'));
  }, [params, checkAuth]);

  const resend = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/auth/verify-email/resend`, { email }, { withCredentials: true }); } catch { /* generic UI either way */ }
    setBusy(false); setResent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" style={FONT} data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8 space-y-4 text-center">
        {state === 'working' && <><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#002FA7]" /><p className="text-sm text-slate-600">Verifying your e-mail…</p></>}
        {state === 'ok' && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
            <h1 className="text-2xl font-bold text-slate-900 mt-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>E-mail verified</h1>
            <p className="text-sm text-slate-600 mt-2">Your company account is active. You can now dispatch loads, add drivers and run payroll.</p>
            <Link to={user ? '/dashboard' : '/login'} className="inline-block mt-5 px-5 py-2.5 rounded-md bg-[#002FA7] text-white text-sm font-semibold" data-testid="verify-continue">{user ? 'Go to dashboard' : 'Sign in'}</Link>
          </div>
        )}
        {state === 'fail' && (
          <div data-testid="verify-failed">
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="text-2xl font-bold text-slate-900 mt-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Link invalid or expired</h1>
            <p className="text-sm text-slate-600 mt-2">Verification links work once and expire after 24 hours. Enter your e-mail to get a new one.</p>
            {resent ? (
              <p className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="verify-resent">If an unverified account exists for that address, a new verification e-mail is on its way.</p>
            ) : (
              <form onSubmit={resend} className="mt-4 flex gap-2">
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="verify-resend-email" />
                <button type="submit" disabled={busy} className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold disabled:opacity-60" data-testid="verify-resend-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Resend</button>
              </form>
            )}
            <Link to="/login" className="inline-block mt-4 text-sm text-[#002FA7] font-semibold" data-testid="verify-back-login">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
